import { useState, useCallback } from 'react';
import { CourierPackageApi } from '@shared/sdk-v1/apis';
import { CourierPackage } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';

interface UsePackagesOptions {
  limit?: number;
  skip?: number;
  where?: Record<string, any>;
  order?: string[];
  append?: boolean;
  searchTerm?: string;
}

interface UsePackagesReturn {
  packages: CourierPackage[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  fetchPackages: (options?: UsePackagesOptions) => Promise<void>;
  searchPackages: (searchTerm: string) => Promise<void>;
  refresh: () => Promise<void>;
  hasPaymentGateway: boolean;
}

export const usePackages = (
  initialOptions?: UsePackagesOptions
): UsePackagesReturn => {
  const [packages, setPackages] = useState<CourierPackage[]>([]);
  const [allPackages, setAllPackages] = useState<CourierPackage[]>([]); // Store all packages for local search
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const { user, courier } = useAuth();

  // Determine payment gateway from already-loaded courier data
  const hasPaymentGateway = Boolean(
    courier?.stripeCredentials?.isStripeApiEnabled ||
    courier?.wipayCredentials?.isWipayApiEnabled ||
    courier?.courierSetting?.isFygaroApiEnabled
  );

  // Local search function
  const searchLocally = useCallback(
    (searchTerm: string): CourierPackage[] => {
      if (!searchTerm.trim()) {
        return allPackages;
      }

      const searchLower = searchTerm.toLowerCase();
      return allPackages.filter((pkg) => {
        const trackingNo = pkg.trackingNo?.toLowerCase() || '';
        const description = pkg.description?.toLowerCase() || '';

        return (
          trackingNo.includes(searchLower) || description.includes(searchLower)
        );
      });
    },
    [allPackages]
  );

  // Search packages function
  const searchPackages = useCallback(
    async (searchTerm: string) => {
      if (!user?.id || !courier?.id) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // First try local search
        const localResults = searchLocally(searchTerm);

        // If we have local results and the search term is not too short, use local results
        if (localResults.length > 0 && searchTerm.length >= 2) {
          setPackages(localResults);
          setTotalCount(localResults.length);
          setIsLoading(false);
          return;
        }

        // If no local results or search term is too short, search via API
        const packageApi = new CourierPackageApi(apiConfig());

        // Build the base filter conditions
        const baseConditions = [
          { ownerId: user.id },
          { courierId: courier.id },
        ];

        // Add search conditions for API
        const searchConditions = {
          or: [
            { trackingNo: { like: `%${searchTerm}%` } },
            { description: { like: `%${searchTerm}%` } },
          ],
        };

        // Construct the filter object
        const filter = {
          where: {
            and: [...baseConditions, searchConditions],
          },
          include: ['invoice'],
          limit: 100, // Limit search results
          order: ['createdOn DESC'],
        };

        // Convert filter to string as required by the API
        const filterString = JSON.stringify(filter);

        // Fetch packages from API
        const response = await packageApi.courierPackageFind({
          filter: filterString,
        });

        // Get total count
        const countResponse = await packageApi.courierPackageCount({
          where: JSON.stringify(filter.where),
        });

        setPackages(response);
        setTotalCount(countResponse.count || 0);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to search packages')
        );
        console.error('Error searching packages:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id, courier?.id, searchLocally]
  );

  const fetchPackages = useCallback(
    async (options?: UsePackagesOptions) => {
      if (!user?.id || !courier?.id) {
        return;
      }

      const packageApi = new CourierPackageApi(apiConfig());
      setIsLoading(true);
      setError(null);

      try {
        // Build the base filter conditions
        const baseConditions = [
          { ownerId: user.id },
          { courierId: courier.id },
        ];

        // Add additional filter conditions if provided (excluding search)
        let additionalConditions: any[] = [];
        if (options?.where) {
          if (options.where.and && Array.isArray(options.where.and)) {
            // Filter out search conditions from additional conditions
            additionalConditions = options.where.and.filter((condition) => {
              // Remove search conditions as they're handled separately
              return (
                !condition.or ||
                !condition.or.some(
                  (orCondition: any) =>
                    orCondition.trackingNo || orCondition.description
                )
              );
            });
          } else {
            // Check if it's a search condition
            const isSearchCondition =
              options.where.or &&
              options.where.or.some(
                (orCondition: any) =>
                  orCondition.trackingNo || orCondition.description
              );
            if (!isSearchCondition) {
              additionalConditions = [options.where];
            }
          }
        }

        // Construct the filter object
        const filter = {
          where: {
            and: [...baseConditions, ...additionalConditions],
          },
          include: ['invoice'], // Include the invoice relation
          limit: options?.limit,
          skip: options?.skip,
          order: options?.order,
        };

        // Convert filter to string as required by the API
        const filterString = JSON.stringify(filter);

        // Fetch packages
        const response = await packageApi.courierPackageFind({
          filter: filterString,
        });

        // Get total count
        const countResponse = await packageApi.courierPackageCount({
          where: JSON.stringify(filter.where),
        });

        // Update packages based on append flag
        setPackages((prevPackages) => {
          const newPackages = options?.append
            ? [...prevPackages, ...response]
            : response;

          // Update allPackages for local search (only on initial load or refresh)
          if (!options?.append) {
            setAllPackages(response);
          }

          return newPackages;
        });

        setTotalCount(countResponse.count || 0);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to fetch packages')
        );
        console.error('Error fetching packages:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id, courier?.id]
  );

  const refresh = useCallback(() => {
    return fetchPackages({ ...initialOptions, append: false });
  }, [fetchPackages, initialOptions]);

  return {
    packages,
    isLoading,
    error,
    totalCount,
    fetchPackages,
    searchPackages,
    refresh,
    hasPaymentGateway,
  };
};

export default usePackages;
